import { useState, useEffect, useCallback } from "react";
import { LoadingScreen, NoAccessScreen } from "../../components";
import { AppFeature } from "../../Constant";
import { usePagePermission } from "../../Hook";
import { FetchReportByInquiry_api } from "../../Api/Reports/fetch_report_by_inquiry";
import { useDebounce } from "use-debounce";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { FileText, Download } from "lucide-react";
import { Select, Spin } from "antd";
import { downloadExcel } from "../../lib/excelGenerator";
import { FetchInquiryNumber_api } from "../../Api";

const { Option } = Select;

export const ReportByInquery = () => {
  const { isLoading: permissionLoading, hasPermission } = usePagePermission(
    AppFeature.REPORT_BY_INQUERY,
    ["admin", "cashier", "manager"],
  );

  const [selectedInquiryId, setSelectedInquiryId] = useState("");
  const [reportData, setReportData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inquiryData, setInquiryData] = useState<any[]>([]);
  const [initialInquiryData, setInitialInquiryData] = useState<any[]>([]);
  const [searchInquiry, setSearchInquiry] = useState("");
  const [debouncedSearch] = useDebounce(searchInquiry, 500);

  const fetchInitialInquiries = async () => {
    const response = await FetchInquiryNumber_api(null, false, 1, 10);
    if (response && response.inquiryData) {
      setInitialInquiryData(response.inquiryData);
      setInquiryData(response.inquiryData);
    }
  };

  const handleSearch = (value: string) => {
    const trimmed = value.trim().toLowerCase();
    setSearchInquiry(value);

    if (!trimmed) {
      setInquiryData(initialInquiryData);
      return;
    }

    const localMatches = initialInquiryData.filter((inq) =>
      inq.inquiryNumber.toLowerCase().includes(trimmed),
    );

    if (localMatches.length > 0) {
      setInquiryData(localMatches);
    }
  };

  useEffect(() => {
    fetchInitialInquiries();
  }, []);

  useEffect(() => {
    const trimmed = debouncedSearch.trim().toLowerCase();
    if (!trimmed) {
      setInquiryData(initialInquiryData);
      return;
    }

    const localMatches = initialInquiryData.filter((inq) =>
      inq.inquiryNumber.toLowerCase().includes(trimmed),
    );

    if (localMatches.length === 0) {
      const fetchFromBE = async () => {
        const response: any = await FetchInquiryNumber_api(
          trimmed,
          false,
          1,
          10,
        );
        if (response && response.inquiryData) {
          setInquiryData(response.inquiryData);
        }
      };
      fetchFromBE();
    }
  }, [debouncedSearch]);

  const fetchReport = async (inquiryId: string) => {
    if (!inquiryId) {
      alert("Please select an inquiry");
      return;
    }
    setLoading(true);
    const response = await FetchReportByInquiry_api(inquiryId);
    if (response.success) {
      setReportData(response.data);
    } else {
      alert(response.msg || "Failed to fetch report");
    }
    setLoading(false);
  };

  const toNumber = (value: any): number =>
    Number(String(value ?? "0").replace(/,/g, "")) || 0;

  const calculateTotal = () => {
    const total = reportData.reduce(
      (acc, curr) => acc + toNumber(curr.amount),
      0,
    );
    return new Intl.NumberFormat("en-LK", {
      style: "currency",
      currency: "LKR",
      minimumFractionDigits: 2,
    }).format(total);
  };

  const handleDownloadExcel = () => {
    const headers = [
      "Slip Date",
      "Customer Name",
      "Slip ID",
      "Method",
      "Amount",
    ];
    const data = reportData.map((row) => [
      row.slip_date,
      row.customer_name,
      row.slip_id,
      row.payment_method,
      toNumber(row.amount),
    ]);
    const inquiry = inquiryData.find((i) => i.id === selectedInquiryId);
    const title = `Inquiry Payment Report - ${inquiry ? inquiry.inquiryNumber : ""}`;
    downloadExcel(
      title, 
      headers, 
      data, 
      `${title}`, 
      calculateTotal(),
      undefined,
      inquiry ? `Total Inquiry: Rs. ${toNumber(inquiry.totalamount).toLocaleString()}` : ""
    );
  };

  if (permissionLoading) {
    return <LoadingScreen messageQueue={"Loading..."} />;
  }

  if (!hasPermission) {
    return <NoAccessScreen />;
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center px-6 pb-5">
        <h1 className="text-2xl font-semibold">Inquiry Payment Report</h1>
        <div className="flex gap-3">
          <Button 
            onClick={handleDownloadExcel} 
            className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white border-none shadow-sm px-4 h-10 rounded-lg transition-all"
            disabled={reportData.length === 0}
          >
            <Download className="w-4 h-4 text-white" />
            <span className="font-medium text-sm">Excel</span>
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white mx-6 mb-6 p-4 rounded-xl shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4">
        <div>
          <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wider">
            Search Inquiry
          </p>
          <Select
            showSearch
            placeholder="Type inquiry number..."
            className="w-full"
            onSearch={handleSearch}
            onChange={(val) => {
                    setSelectedInquiryId(val);
                    fetchReport(val);
                  }}
                  filterOption={false}
                  notFoundContent={loading ? <Spin size="small" /> : <div className="p-4 text-gray-400 text-sm text-center">No inquiries found</div>}
                >
                  {inquiryData.map((i) => (
                    <Option key={i.id} value={i.id}>
                      <div className="flex justify-between items-center py-1">
                        <span className="font-semibold text-gray-800">{i.inquiryNumber}</span>
                        <span className="text-xs bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-mono">Rs. {toNumber(i.totalamount).toLocaleString()}</span>
                      </div>
                    </Option>
                  ))}
          </Select>
        </div>
      </div>

      {reportData.length > 0 && (
        <div className="px-6 pb-6">
          {/* Green Summary Card */}
          <div className="bg-emerald-600 rounded-xl shadow-md overflow-hidden mb-6 p-6 text-white text-sm">
            <div className="flex gap-12">
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL INQUIRY AMOUNT</p>
                <p className="text-2xl font-bold">
                  Rs. {toNumber(inquiryData.find(i => i.id === selectedInquiryId)?.totalamount || 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-bold opacity-80 uppercase tracking-wider mb-1">TOTAL PAID AMOUNT</p>
                <p className="text-2xl font-bold">{calculateTotal()}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col">
            <div className="flex-grow overflow-auto max-h-[calc(100vh-320px)]">
              {loading ? (
                <div className="flex justify-center items-center py-20">
                  <Spin size="large" />
                </div>
              ) : (
                <Table className="min-w-max text-sm">
                  <TableHeader className="bg-gray-100/50">
                    <TableRow className="border-b">
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Date</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Customer</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Reference ID</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700">Payment Method</TableHead>
                      <TableHead className="py-3 px-4 font-semibold text-gray-700 text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                      {reportData.map((row, index) => (
                        <TableRow 
                          key={`${row.slip_id}-${index}`}
                          className="border-b last:border-0 hover:bg-gray-50"
                        >
                          <TableCell className="px-4 py-3">{row.slip_date}</TableCell>
                          <TableCell className="px-4 py-3">{row.customer_name}</TableCell>
                          <TableCell className="px-4 py-3 text-gray-500 font-mono text-xs">{row.slip_id}</TableCell>
                          <TableCell className="px-4 py-3 capitalize">{row.payment_method}</TableCell>
                          <TableCell className="px-4 py-3 text-right font-medium">
                             {new Intl.NumberFormat("en-LK", {
                                style: "currency",
                                currency: "LKR",
                                minimumFractionDigits: 2,
                              }).format(toNumber(row.amount))}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        </div>
      )}

      {reportData.length === 0 && !loading && selectedInquiryId && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>No payment activities listed for this inquiry.</p>
          </div>
        </div>
      )}
      {reportData.length === 0 && !loading && !selectedInquiryId && (
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden flex flex-col items-center justify-center py-20 text-gray-500">
            <p>Select an inquiry above to analyze payment performance.</p>
          </div>
        </div>
      )}
    </div>
  );
};
